import { useMemo, useState, type FormEvent } from "react";
import PageMeta from "@/components/base/PageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/base/NewsCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { news } from "@/mocks/news";
import type { NewsArticle } from "@/types";

const PAGE_SIZE = 4;

export default function NewsList() {
  const [keyword, setKeyword] = useState("");
  const [input, setInput] = useState("");
  const [page, setPage] = useState(1);

  const allNews = useMemo(
    () => [...(news as NewsArticle[])].sort((a, b) => (a.date < b.date ? 1 : -1)),
    []
  );

  const filtered = useMemo(() => {
    const key = keyword.trim().toLowerCase();
    if (!key) return allNews;
    return allNews.filter((article) => {
      const haystack = [article.title, article.summary, article.author, article.tags.join(" ")]
        .join(" ")
        .toLowerCase();
      return haystack.includes(key);
    });
  }, [allNews, keyword]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    setKeyword(input);
    setPage(1);
  };

  const handleReset = () => {
    setInput("");
    setKeyword("");
    setPage(1);
  };

  return (
    <div className="pt-16 md:pt-[72px]">
      <PageMeta
        title="佐山爱最新明星动态"
        description="佐山爱最新明星动态一览，涵盖新片资讯、活动现场、幕后专访与影展报道，第一时间了解偶像的最新消息。"
        keywords="佐山爱动态,佐山爱最新资讯,明星动态,影视资讯,佐山爱新闻"
      />

      <section className="px-5 md:px-10 pt-10 md:pt-14 pb-8 md:pb-10 bg-background-100">
        <div className="mb-5">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "明星动态" }]} />
        </div>
        <span className="text-xs font-medium tracking-[0.2em] uppercase text-accent-600">
          News & Updates
        </span>
        <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground-950 mt-3 mb-4">
          佐山爱明星动态
        </h1>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl mb-6">
          汇聚佐山爱的最新资讯 —— 新片发布、活动现场、幕后专访与影展报道。
          你可以通过关键词搜索快速定位感兴趣的动态。
        </p>

        <form
          onSubmit={handleSearch}
          className="flex flex-col sm:flex-row gap-3 max-w-xl"
          role="search"
        >
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-base" />
            <input
              type="text"
              name="keyword"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="搜索动态标题、关键词…"
              className="w-full pl-10 pr-4 py-3 rounded-md text-sm bg-background-50 border border-background-200 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-search-line" />
            搜索
          </button>
        </form>

        {keyword && (
          <div className="mt-4 flex items-center gap-2 flex-wrap text-sm">
            <span className="text-foreground-500">
              搜索关键词：<span className="text-accent-700 font-medium">{keyword}</span>
            </span>
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex items-center gap-1 text-xs text-foreground-400 hover:text-accent-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-circle-line" />
              清除搜索
            </button>
          </div>
        )}
      </section>

      <section className="px-5 md:px-10 py-10 md:py-14">
        <div className="flex items-center justify-between gap-4 mb-6">
          <h2 className="font-heading font-semibold text-lg text-foreground-950">
            {keyword ? "搜索结果" : "最新动态"}
          </h2>
          <span className="text-sm text-foreground-500">共 {filtered.length} 条</span>
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {pageItems.map((article) => (
                <NewsCard key={article.id} article={article} variant="grid" />
              ))}
            </div>
            <Pagination currentPage={safePage} totalPages={totalPages} onPageChange={setPage} />
          </>
        ) : (
          <EmptyState
            icon="ri-search-eye-line"
            title="暂无相关动态"
            description={`没有找到与「${keyword}」相关的明星动态，试试换一个关键词，或重置搜索。`}
            actionLabel="重置搜索"
            onAction={handleReset}
          />
        )}
      </section>
    </div>
  );
}